import { Button, useTranslation } from '@hbs-us/ui';
import { useContext, useState } from 'react';

import { SessionContext } from '../../auth/useSession.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import type { TimesheetEditRequest } from './contractsMirror.js';
import {
  useApproveEditRequestMutation,
  useRejectEditRequestMutation,
} from './useApprovalMutation.js';

/**
 * The edit-request (time-correction) review list (UI-SPEC Timesheets): each pending request shows the
 * ORIGINAL entry next to the employee's PROPOSED times — a side-by-side diff — with Approve / Reject.
 * Approve applies the proposed times (the SERVER re-runs the lunch math); Reject keeps the original
 * and is routed through the `_money/ConfirmDialog` ("Reject this time correction?").
 *
 * THE GUARDRAIL (D-03 / T-22-payroll): the proposed/original `hours` shown here are SERVER-COMPUTED.
 * This component does NO arithmetic — it renders the diff and fires the approve/reject mutation; the
 * server owns the lunch/overtime recompute on approve.
 *
 * Accessibility (A11Y-01): each request is a labelled region, the diff columns are headed, the actions
 * are 44px buttons, and the destructive reject is focus-trapped + Esc-cancellable via ConfirmDialog.
 */

/**
 * A punch is an instant, so it is shown on the company's clock: the zone the manager approving it
 * and the crew who worked it both read. It used to print the UTC slice, so an 8:00 punch in New
 * York read 12:00. With no company zone on the session, the browser's own zone is the next best.
 */
function fmtTime(iso: string | null, timeZone: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const clock = (zone: string | undefined) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone: zone,
    }).formatToParts(d);
    const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return `${part('hour')}:${part('minute')}`;
  };
  try {
    return clock(timeZone);
  } catch {
    // An unknown zone name throws; the browser zone still beats UTC.
    return clock(undefined);
  }
}

function fmtHours(value: number): string {
  return Number(value || 0).toFixed(2);
}

export interface EditRequestReviewProps {
  requests: TimesheetEditRequest[];
}

export function EditRequestReview({ requests }: EditRequestReviewProps) {
  const { t } = useTranslation();
  const approve = useApproveEditRequestMutation();
  const reject = useRejectEditRequestMutation();
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  // Read without useSession's throw: this list also renders in tests and previews with no provider.
  const timeZone = useContext(SessionContext)?.session?.companySettings?.timeZone;

  if (requests.length === 0) {
    return (
      <section
        aria-label={t('timesheets:requests.regionLabel')}
        className="rounded-md border border-border bg-card p-6"
      >
        <h2 className="font-semibold text-foreground text-sm">
          {t('timesheets:requests.heading')}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm">{t('timesheets:requests.empty')}</p>
      </section>
    );
  }

  return (
    <section
      aria-label={t('timesheets:requests.regionLabel')}
      className="rounded-md border border-border bg-card p-6"
    >
      <h2 className="mb-4 font-semibold text-foreground text-sm">
        {t('timesheets:requests.heading')}
      </h2>
      <ul className="flex flex-col gap-4">
        {requests.map((req) => (
          <li
            key={req.id}
            className="rounded-md border border-border p-4"
            aria-label={t('timesheets:requests.itemLabel', { name: req.employeeName })}
          >
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium text-foreground text-sm">
                {req.employeeName} · {req.jobName}
              </p>
              <p className="text-muted-foreground text-xs">{req.proposed.date}</p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-md bg-muted/40 p-3">
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                  {t('timesheets:requests.original')}
                </p>
                <p className="font-mono text-foreground text-sm">
                  {fmtTime(req.original.clockInAt, timeZone)} –{' '}
                  {fmtTime(req.original.clockOutAt, timeZone)}
                </p>
                <p className="font-mono text-muted-foreground text-xs">
                  {fmtHours(req.original.hours)} {t('timesheets:requests.hours')}
                </p>
              </div>
              <div className="rounded-md bg-secondary/40 p-3">
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                  {t('timesheets:requests.proposed')}
                </p>
                <p className="font-mono text-foreground text-sm">
                  {fmtTime(req.proposed.clockInAt, timeZone)} –{' '}
                  {fmtTime(req.proposed.clockOutAt, timeZone)}
                </p>
                <p className="font-mono text-muted-foreground text-xs">
                  {fmtHours(req.proposed.hours)} {t('timesheets:requests.hours')}
                </p>
              </div>
            </div>

            {req.reason ? (
              <p className="mt-3 text-muted-foreground text-sm">
                <span className="font-medium">{t('timesheets:requests.reason')}:</span> {req.reason}
              </p>
            ) : null}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => approve.mutate({ requestId: req.id })}
                disabled={approve.isPending}
              >
                {t('timesheets:requests.approve')}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setRejectingId(req.id)}
                disabled={reject.isPending}
              >
                {t('timesheets:requests.reject')}
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <ConfirmDialog
        open={rejectingId !== null}
        heading={t('timesheets:requests.confirmRejectHeading')}
        body={t('timesheets:requests.confirmRejectBody')}
        confirmLabel={t('timesheets:requests.reject')}
        cancelLabel={t('timesheets:requests.cancel')}
        confirmPending={reject.isPending}
        onCancel={() => setRejectingId(null)}
        onConfirm={() => {
          if (rejectingId !== null) {
            reject.mutate({ requestId: rejectingId });
            setRejectingId(null);
          }
        }}
      />
    </section>
  );
}
